'use client';

import { TextLink, Grid } from '@petsmart-ui/sparky';

import { clsx, useCart, usePageLayout, useDeviceType, DEVICE_TYPE } from '@libs/common-next';
import type { StorageUserSession } from '@libs/common-next';
import { NAVIGATION_URLS } from '@libs/core';
import { handleLogout } from '@libs/experience-api';
import './LoggedIn.scss';

const navigationUrls = NAVIGATION_URLS.velocity.account;

export function LoggedIn({ firstName = '', loyaltyPoints = 0 }: Readonly<StorageUserSession>) {
    const { flags } = usePageLayout();
    const { clearCart } = useCart();
    const deviceType = useDeviceType();
    const isLoyaltyAccountMenu = flags.loyaltyAccountMenu;

    const wrapperClasses = clsx('legacy-loggedin', isLoyaltyAccountMenu ? 'loyalty-account-menu-enabled' : '');

    const onLogout = async () => {
        if (await handleLogout()) {
            clearCart();

            window.location.href = '/sign-in';
        }
    };

    if (!firstName) {
        return null;
    }

    return (
        <div className={wrapperClasses} data-testid="logged-in">
            <div className="legacy-loggedin__button">
                {deviceType === DEVICE_TYPE.DESKTOP ? (
                    <TextLink href={navigationUrls.treatOffers}>
                        <span className="legacy-loggedin__greetings">
                            Hi,
                            <span className="legacy-loggedin__user-name" data-cs-mask>
                                {firstName}
                            </span>
                        </span>
                        <span className="legacy-account__caret" />
                    </TextLink>
                ) : (
                    <>
                        <span>
                            Hi, <span data-cs-mask>{firstName}</span>
                        </span>
                        <span className="legacy-account__caret" />
                    </>
                )}
            </div>

            <div className="legacy-loggedin__submenu">
                <a
                    className="points-link"
                    href={navigationUrls.treatOffers}
                    title="Go to: View your rewards and offers"
                >
                    <div className="points">
                        <span className="point-count">
                            {` ${new Intl.NumberFormat('en-US', {
                                maximumFractionDigits: 0,
                            }).format(loyaltyPoints || 0)} `}
                        </span>
                        pts.
                        <span className="account">{isLoyaltyAccountMenu ? 'Treats Rewards' : 'Account'}</span>
                    </div>
                </a>

                <Grid variant="1-2up" gap="none">
                    <Grid.Item>
                        <TextLink href={navigationUrls.aboutYou}>About you</TextLink>
                    </Grid.Item>
                    <Grid.Item>
                        <TextLink href={navigationUrls.creditCards}>Credit cards</TextLink>
                    </Grid.Item>
                    <Grid.Item>
                        <TextLink href={navigationUrls.servicesGrooming}>Appointments</TextLink>
                    </Grid.Item>
                    <Grid.Item>
                        <TextLink href={navigationUrls.autoShip}>Autoship</TextLink>
                    </Grid.Item>
                    <Grid.Item>
                        <TextLink href={navigationUrls.orderHistory}>Recent orders</TextLink>
                    </Grid.Item>
                    <Grid.Item>
                        <TextLink href={navigationUrls.offers}>Offers</TextLink>
                    </Grid.Item>
                </Grid>
                <TextLink className="logout-link" onClick={onLogout}>
                    Logout
                </TextLink>
            </div>

            <div className="dropdown-accent" />
        </div>
    );
}
